import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Breadcrumbs from "../../styles/components/Breadcrumbs";
import { API_BASE } from "../../config/apiBase";
import "../../styles/pages/content/_administration-analytics.scss";

// ─── Constants ────────────────────────────────────────────────────────────────
const RANGE_OPTIONS = [
    { value: "daily", label: "Daily" },
    { value: "weekly", label: "Weekly" },
    { value: "monthly", label: "Monthly" },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (v) => (v == null ? "—" : Number(v).toLocaleString());
const fmtP = (v) => (v == null ? "—" : `${Number(v).toFixed(1)}%`);

function fmtDateLabel(value, range) {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    if (range === "monthly")
        return d.toLocaleDateString([], { month: "short", year: "2-digit" });
    return d.toLocaleDateString([], { month: "short", day: "2-digit" });
}

// ─── Icons ────────────────────────────────────────────────────────────────────
const IconTrendUp = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M1 10L5 6l3 3 5-5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M9 4h4v4"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconTrendDown = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 14 14"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M1 4L5 8l3-3 5 5"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
        <path
            d="M9 10h4V6"
            stroke="currentColor"
            strokeWidth="1.4"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const IconWarn = () => (
    <svg
        width="13"
        height="13"
        viewBox="0 0 13 13"
        fill="none"
        aria-hidden="true"
    >
        <path
            d="M6.5 1.5L12 11H1L6.5 1.5z"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinejoin="round"
        />
        <path
            d="M6.5 5v3"
            stroke="currentColor"
            strokeWidth="1.2"
            strokeLinecap="round"
        />
        <circle cx="6.5" cy="9.5" r="0.6" fill="currentColor" />
    </svg>
);

const IconDot = ({ color }) => (
    <span
        className="adm-analytics-dot"
        style={{ background: color }}
        aria-hidden="true"
    />
);

// ─── Micro sparkline ──────────────────────────────────────────────────────────
function Sparkline({
    series,
    color = "currentColor",
    height = 32,
    width = 80,
}) {
    if (!series || series.length < 2) return null;
    const max = Math.max(...series, 1);
    const pts = series
        .map((v, i) => {
            const x = (i / (series.length - 1)) * width;
            const y = height - (v / max) * (height - 4) - 2;
            return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .join(" ");
    return (
        <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            aria-hidden="true"
            className="adm-analytics-sparkline"
        >
            <polyline
                fill="none"
                stroke={color}
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
                points={pts}
            />
        </svg>
    );
}

// ─── KPI card ─────────────────────────────────────────────────────────────────
function KpiCard({
    label,
    value,
    meta,
    delta,
    deltaDir,
    sparkline,
    accent,
    index,
}) {
    return (
        <div
            className={`adm-analytics-kpi adm-analytics-kpi--${accent || "default"}`}
            style={{ "--i": index }}
        >
            <div className="adm-analytics-kpi__top">
                <span className="adm-analytics-kpi__label">{label}</span>
                {delta != null && (
                    <span
                        className={`adm-analytics-kpi__delta adm-analytics-kpi__delta--${deltaDir}`}
                    >
                        {deltaDir === "up" ? (
                            <IconTrendUp />
                        ) : (
                            <IconTrendDown />
                        )}
                        {delta}
                    </span>
                )}
            </div>
            <div className="adm-analytics-kpi__value">{value}</div>
            <div className="adm-analytics-kpi__bottom">
                <span className="adm-analytics-kpi__meta">{meta}</span>
                {sparkline && (
                    <Sparkline series={sparkline} color="currentColor" />
                )}
            </div>
        </div>
    );
}

// ─── Section header ───────────────────────────────────────────────────────────
function SectionHead({ title, pill, children }) {
    return (
        <div className="adm-analytics-section-head">
            <div className="adm-analytics-section-head__left">
                {/* span not h2/h3 — prevents global heading CSS leaking in */}
                <span className="adm-analytics-section-head__title">
                    {title}
                </span>
                {pill && <span className="adm-analytics-pill">{pill}</span>}
            </div>
            {children && (
                <div className="adm-analytics-section-head__right">
                    {children}
                </div>
            )}
        </div>
    );
}

// ─── Line chart (SVG, no deps) ────────────────────────────────────────────────
function LineChart({ series, range, height = 120 }) {
    if (!series || series.length < 2) {
        return (
            <div className="adm-analytics-chart-empty">
                No data for this range
            </div>
        );
    }
    const max = Math.max(...series.map((s) => s.value), 1);
    const W = 1000;
    const H = height;
    const PAD = 6;
    const pts = series.map((pt, i) => ({
        x: PAD + (i / (series.length - 1)) * (W - PAD * 2),
        y: H - PAD - (pt.value / max) * (H - PAD * 2),
        ...pt,
    }));
    const linePts = pts
        .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
        .join(" ");
    const areaPts = [
        `${pts[0].x.toFixed(1)},${H}`,
        ...pts.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`),
        `${pts[pts.length - 1].x.toFixed(1)},${H}`,
    ].join(" ");

    // Show every nth label based on series length
    const step = Math.ceil(series.length / 6);
    const ticks = pts.filter((_, i) => i % step === 0 || i === pts.length - 1);

    return (
        <div className="adm-analytics-linechart">
            <svg
                viewBox={`0 0 ${W} ${H}`}
                preserveAspectRatio="none"
                aria-label="Active users line chart"
                role="img"
            >
                <defs>
                    <linearGradient
                        id="adm-analytics-area-grad"
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                    >
                        <stop
                            offset="0%"
                            stopColor="currentColor"
                            stopOpacity="0.15"
                        />
                        <stop
                            offset="100%"
                            stopColor="currentColor"
                            stopOpacity="0"
                        />
                    </linearGradient>
                </defs>
                <polygon
                    fill="url(#adm-analytics-area-grad)"
                    points={areaPts}
                    opacity="1"
                />
                <polyline
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    points={linePts}
                />
                {pts.map((p, i) => (
                    <circle
                        key={i}
                        cx={p.x}
                        cy={p.y}
                        r="3"
                        fill="currentColor"
                        opacity={i === pts.length - 1 ? 1 : 0}
                    />
                ))}
            </svg>
            <div className="adm-analytics-linechart__ticks">
                {ticks.map((p) => (
                    <span key={p.label}>{fmtDateLabel(p.label, range)}</span>
                ))}
            </div>
        </div>
    );
}

// ─── Bar chart (SVG, no deps) ─────────────────────────────────────────────────
function BarChart({ series }) {
    if (!series || series.length === 0)
        return <div className="adm-analytics-chart-empty">No data</div>;
    const max = Math.max(...series.map((s) => s.value), 1);
    const step = Math.ceil(series.length / 8);
    const tick = series.filter(
        (_, i) => i % step === 0 || i === series.length - 1
    );

    return (
        <div className="adm-analytics-barchart">
            <div className="adm-analytics-barchart__bars">
                {series.map((pt, i) => (
                    <div
                        key={i}
                        className="adm-analytics-barchart__bar"
                        style={{ "--h": `${(pt.value / max) * 100}%` }}
                        title={`${fmtDateLabel(pt.label, "daily")}: ${fmt(pt.value)}`}
                    />
                ))}
            </div>
            <div className="adm-analytics-barchart__ticks">
                {tick.map((pt) => (
                    <span key={pt.label}>
                        {fmtDateLabel(pt.label, "daily")}
                    </span>
                ))}
            </div>
        </div>
    );
}

// ─── Histogram bar ────────────────────────────────────────────────────────────
function HistBar({ label, count, max, accent }) {
    const pct = max > 0 ? (count / max) * 100 : 0;
    return (
        <div className="adm-analytics-hist-row">
            <span className="adm-analytics-hist-row__label">{label}</span>
            <div className="adm-analytics-hist-row__track">
                <div
                    className={`adm-analytics-hist-row__fill adm-analytics-hist-row__fill--${accent || "default"}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
            <span className="adm-analytics-hist-row__val">{fmt(count)}</span>
        </div>
    );
}

// ─── Funnel step ──────────────────────────────────────────────────────────────
function FunnelStep({ label, value, total, index, isLast }) {
    const pct = total > 0 ? ((value / total) * 100).toFixed(1) : null;
    const width = total > 0 ? Math.max((value / total) * 100, 8) : 8;

    return (
        <div className="adm-analytics-funnel-step" style={{ "--i": index }}>
            <div className="adm-analytics-funnel-step__bar-wrap">
                <div
                    className="adm-analytics-funnel-step__bar"
                    style={{ width: `${width}%` }}
                />
            </div>
            <div className="adm-analytics-funnel-step__meta">
                <span className="adm-analytics-funnel-step__label">
                    {label}
                </span>
                <div className="adm-analytics-funnel-step__nums">
                    <strong className="adm-analytics-funnel-step__val">
                        {fmt(value)}
                    </strong>
                    {pct && !isLast && (
                        <span className="adm-analytics-funnel-step__pct">
                            {pct}% of sign-ups
                        </span>
                    )}
                    {isLast && pct && (
                        <span className="adm-analytics-funnel-step__pct adm-analytics-funnel-step__pct--green">
                            {pct}% converted
                        </span>
                    )}
                </div>
            </div>
            {!isLast && (
                <div
                    className="adm-analytics-funnel-step__connector"
                    aria-hidden="true"
                />
            )}
        </div>
    );
}

// ─── Health row ───────────────────────────────────────────────────────────────
function HealthRow({ label, value, isWarning }) {
    return (
        <div
            className={`adm-analytics-health-row${isWarning && value > 0 ? " adm-analytics-health-row--warn" : ""}`}
        >
            <span className="adm-analytics-health-row__label">
                {isWarning && value > 0 && <IconWarn />}
                {label}
            </span>
            <span className="adm-analytics-health-row__val">{fmt(value)}</span>
        </div>
    );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AdministrationAnalytics() {
    const { isAuthenticated, isLoading, getAccessTokenSilently } = useAuth0();

    const [analytics, setAnalytics] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [range, setRange] = useState("daily");

    const apiBase = API_BASE;

    const authedFetch = useCallback(
        async (path, options = {}) => {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: process.env.REACT_APP_AUTH0_AUDIENCE,
                },
            });
            return fetch(`${apiBase}${path}`, {
                ...options,
                headers: {
                    "Content-Type": "application/json",
                    ...(options.headers || {}),
                    Authorization: `Bearer ${token}`,
                },
            });
        },
        [apiBase, getAccessTokenSilently]
    );

    useEffect(() => {
        if (isLoading) return;
        if (!isAuthenticated) {
            setLoading(false);
            setError("Log in to view analytics.");
            return;
        }
        let aborted = false;
        (async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await authedFetch("/admin/analytics", {
                    method: "GET",
                });
                if (!res.ok) {
                    const t = await res.text();
                    throw new Error(t || "Failed to load analytics.");
                }
                const data = await res.json();
                if (!aborted) setAnalytics(data);
            } catch (err) {
                if (!aborted)
                    setError(err?.message || "Failed to load analytics.");
            } finally {
                if (!aborted) setLoading(false);
            }
        })();
        return () => {
            aborted = true;
        };
    }, [authedFetch, isAuthenticated, isLoading]);

    // ── Derived ───────────────────────────────────────────────────────────────
    const kpis = useMemo(() => {
        if (!analytics) return [];
        const { active_users, funnel, recordings_daily } = analytics;
        const last7 = (recordings_daily || [])
            .slice(-7)
            .reduce((s, r) => s + (r.recordings || 0), 0);
        const convRate =
            funnel?.recorded_users && funnel?.total_free_users
                ? (funnel.recorded_users / funnel.total_free_users) * 100
                : null;
        const recSparkline = (recordings_daily || [])
            .slice(-14)
            .map((r) => r.recordings || 0);

        return [
            {
                label: "Weekly active users",
                value: fmt(active_users?.wau),
                meta: "Unique recorders",
                accent: "purple",
            },
            {
                label: "Monthly active users",
                value: fmt(active_users?.mau),
                meta: "Last 30 days",
                accent: "default",
            },
            {
                label: "Free → Pro AI",
                value: fmtP(convRate),
                meta: "Of users who recorded",
                accent: "green",
            },
            {
                label: "Recordings (7d)",
                value: fmt(last7 || 0),
                meta: "Last 7 days",
                accent: "default",
                sparkline: recSparkline,
            },
        ];
    }, [analytics]);

    const activeSeries = useMemo(() => {
        const raw = analytics?.active_users?.[range] || [];
        if (range === "daily")
            return raw.map((r) => ({
                label: r.day,
                value: Number(r.active_users || 0),
            }));
        if (range === "weekly")
            return raw.map((r) => ({
                label: r.week_start,
                value: Number(r.active_users || 0),
            }));
        return raw.map((r) => ({
            label: r.month_start,
            value: Number(r.active_users || 0),
        }));
    }, [analytics, range]);

    const recordingSeries = useMemo(
        () =>
            (analytics?.recordings_daily || []).map((r) => ({
                label: r.day,
                value: Number(r.recordings || 0),
            })),
        [analytics]
    );

    const feedbackDist = analytics?.feedback_distribution || [];
    const topQuestions = analytics?.top_questions || [];
    const planOverview = analytics?.plan_overview;
    const churnRisk = analytics?.churn_risk || [];
    const procHealth = analytics?.processing_health;
    const sessionLength = analytics?.session_length;
    const funnel = analytics?.funnel;

    const feedbackMax = Math.max(...feedbackDist.map((d) => d.count || 0), 1);
    const sessionMax = sessionLength
        ? Math.max(
              sessionLength.under_30s,
              sessionLength.between_30_120,
              sessionLength.between_120_300,
              sessionLength.over_300,
              1
          )
        : 1;

    const planDelta = planOverview?.delta_pct;
    const planDeltaLabel =
        planDelta == null
            ? "—"
            : `${planDelta >= 0 ? "+" : ""}${planDelta.toFixed(1)}%`;

    const healthErrors = procHealth?.error_count || 0;

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <div className="adm-analytics" id="administration-analytics">
            <div className="adm-analytics__inner">
                <div>
                    <Breadcrumbs
                        items={[
                            { label: "Home", href: "/" },
                            {
                                label: "Administration",
                                href: "/administration",
                            },
                            {
                                label: "Analytics",
                                href: "/administration/analytics",
                            },
                        ]}
                    />

                    {/* ── Header ── */}
                    <div className="adm-analytics__header">
                        <div>
                            {/* <p className="adm-analytics__eyebrow">
                                Administration
                            </p> */}
                            <h1 className="adm-analytics__title">Analytics</h1>
                            <p className="adm-analytics__sub">
                                Monitor engagement, conversion, and platform
                                health at a glance.
                            </p>
                        </div>
                        {healthErrors > 0 && (
                            <div
                                className="adm-analytics-health-alert"
                                role="alert"
                            >
                                <IconWarn /> {healthErrors} processing error
                                {healthErrors !== 1 ? "s" : ""}
                            </div>
                        )}
                    </div>
                </div>

                {error && (
                    <p className="adm-analytics-notice adm-analytics-notice--error">
                        {error}
                    </p>
                )}

                {loading ? (
                    /* Skeleton */
                    <div className="adm-analytics-skeleton">
                        <div className="adm-analytics-skeleton__kpis">
                            {[0, 1, 2, 3].map((i) => (
                                <div
                                    className="adm-analytics-skeleton__kpi"
                                    key={i}
                                    style={{ "--i": i }}
                                />
                            ))}
                        </div>
                        <div className="adm-analytics-skeleton__chart" />
                        <div className="adm-analytics-skeleton__grid">
                            {[0, 1].map((i) => (
                                <div
                                    className="adm-analytics-skeleton__panel"
                                    key={i}
                                    style={{ "--i": i }}
                                />
                            ))}
                        </div>
                    </div>
                ) : (
                    <>
                        {/* ── KPI strip ── */}
                        <div className="adm-analytics-kpi-grid">
                            {kpis.map((k, i) => (
                                <KpiCard key={k.label} index={i} {...k} />
                            ))}
                        </div>

                        {/* ── Active users chart ── */}
                        <div className="adm-analytics-panel">
                            <SectionHead
                                title="Active users"
                                pill={
                                    range === "daily"
                                        ? "Last 30 days"
                                        : range === "weekly"
                                          ? "Last 12 weeks"
                                          : "Last 12 months"
                                }
                            >
                                <div
                                    className="adm-analytics-range-tabs"
                                    role="group"
                                    aria-label="Time range"
                                >
                                    {RANGE_OPTIONS.map((o) => (
                                        <button
                                            key={o.value}
                                            type="button"
                                            className={`adm-analytics-range-tab${range === o.value ? " is-active" : ""}`}
                                            onClick={() => setRange(o.value)}
                                        >
                                            {o.label}
                                        </button>
                                    ))}
                                </div>
                            </SectionHead>
                            <LineChart series={activeSeries} range={range} />
                        </div>

                        {/* ── Recording volume ── */}
                        <div className="adm-analytics-panel">
                            <SectionHead
                                title="Recording volume"
                                pill="Daily totals"
                            />
                            <BarChart series={recordingSeries} />
                            <p className="adm-analytics-chart-note">
                                Track recordings created each day to anticipate
                                usage spikes and content demand.
                            </p>
                        </div>

                        {/* ── Two-col: Funnel + Health ── */}
                        <div className="adm-analytics-grid adm-analytics-grid--2">
                            <div className="adm-analytics-panel">
                                <SectionHead
                                    title="Conversion funnel"
                                    pill="3-step"
                                />
                                <div className="adm-analytics-funnel">
                                    <FunnelStep
                                        label="Sign-ups"
                                        value={funnel?.total_free_users}
                                        total={funnel?.total_free_users}
                                        index={0}
                                    />
                                    <FunnelStep
                                        label="First recording"
                                        value={funnel?.recorded_users}
                                        total={funnel?.total_free_users}
                                        index={1}
                                    />
                                    <FunnelStep
                                        label="Upgraded to Pro AI"
                                        value={funnel?.pro_users}
                                        total={funnel?.total_free_users}
                                        index={2}
                                        isLast
                                    />
                                </div>
                            </div>

                            <div className="adm-analytics-panel">
                                <SectionHead
                                    title="Platform health"
                                    pill={
                                        healthErrors > 0
                                            ? "Issues detected"
                                            : "All clear"
                                    }
                                />
                                <div className="adm-analytics-health">
                                    <HealthRow
                                        label="Transcript pending"
                                        value={procHealth?.transcript_pending}
                                    />
                                    <HealthRow
                                        label="Feedback pending"
                                        value={procHealth?.feedback_pending}
                                    />
                                    <HealthRow
                                        label="Processing errors"
                                        value={procHealth?.error_count}
                                        isWarning
                                    />
                                </div>
                                <div className="adm-analytics-health-legend">
                                    <span>
                                        <IconDot color="#0d7a4e" /> Normal
                                    </span>
                                    <span>
                                        <IconDot color="#b91c1c" /> Needs
                                        attention
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* ── Two-col: Feedback dist + Plan overview ── */}
                        <div className="adm-analytics-grid adm-analytics-grid--2">
                            <div className="adm-analytics-panel">
                                <SectionHead
                                    title="AI feedback score distribution"
                                    pill="0–10 bands"
                                />
                                <div className="adm-analytics-histogram">
                                    {feedbackDist.length > 0 ? (
                                        feedbackDist.map((item) => (
                                            <HistBar
                                                key={item.band}
                                                label={item.band}
                                                count={item.count}
                                                max={feedbackMax}
                                                accent="purple"
                                            />
                                        ))
                                    ) : (
                                        <p className="adm-analytics-empty">
                                            No feedback data yet.
                                        </p>
                                    )}
                                </div>
                            </div>

                            <div className="adm-analytics-panel">
                                <SectionHead
                                    title="Plan overview"
                                    pill="Pro AI"
                                />
                                <div className="adm-analytics-plan-stats">
                                    <div className="adm-analytics-plan-stat">
                                        <span className="adm-analytics-plan-stat__label">
                                            This month
                                        </span>
                                        <span className="adm-analytics-plan-stat__val">
                                            {fmt(
                                                planOverview?.current_month_pro
                                            )}
                                        </span>
                                    </div>
                                    <div className="adm-analytics-plan-stat">
                                        <span className="adm-analytics-plan-stat__label">
                                            Last month
                                        </span>
                                        <span className="adm-analytics-plan-stat__val">
                                            {fmt(planOverview?.last_month_pro)}
                                        </span>
                                    </div>
                                    <div
                                        className={`adm-analytics-plan-stat adm-analytics-plan-stat--delta${planDelta < 0 ? " adm-analytics-plan-stat--down" : " adm-analytics-plan-stat--up"}`}
                                    >
                                        <span className="adm-analytics-plan-stat__label">
                                            Month-on-month
                                        </span>
                                        <span className="adm-analytics-plan-stat__val">
                                            {planDelta != null &&
                                                (planDelta >= 0 ? (
                                                    <IconTrendUp />
                                                ) : (
                                                    <IconTrendDown />
                                                ))}
                                            {planDeltaLabel}
                                        </span>
                                    </div>
                                </div>
                                <div className="adm-analytics-session">
                                    <SectionHead
                                        title="Session length"
                                        pill="Duration buckets"
                                    />
                                    {sessionLength ? (
                                        <div className="adm-analytics-histogram adm-analytics-histogram--sm">
                                            <HistBar
                                                label="< 30s"
                                                count={sessionLength.under_30s}
                                                max={sessionMax}
                                                accent="blue"
                                            />
                                            <HistBar
                                                label="30s–2m"
                                                count={
                                                    sessionLength.between_30_120
                                                }
                                                max={sessionMax}
                                                accent="blue"
                                            />
                                            <HistBar
                                                label="2–5m"
                                                count={
                                                    sessionLength.between_120_300
                                                }
                                                max={sessionMax}
                                                accent="blue"
                                            />
                                            <HistBar
                                                label="5m+"
                                                count={sessionLength.over_300}
                                                max={sessionMax}
                                                accent="blue"
                                            />
                                        </div>
                                    ) : (
                                        <p className="adm-analytics-empty">
                                            No session data.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* ── Top questions table ── */}
                        <div className="adm-analytics-panel">
                            <SectionHead
                                title="Most-practised questions"
                                pill="Top 10"
                            />
                            <div className="adm-analytics-table" role="table">
                                <div
                                    className="adm-analytics-table__head"
                                    role="row"
                                >
                                    <span role="columnheader">Question</span>
                                    <span role="columnheader">Recordings</span>
                                    <span role="columnheader">
                                        Completion rate
                                    </span>
                                </div>
                                {topQuestions.length > 0 ? (
                                    topQuestions.map((row, i) => (
                                        <div
                                            className="adm-analytics-table__row"
                                            key={row.question_slug}
                                            role="row"
                                            style={{ "--i": i }}
                                        >
                                            <span className="adm-analytics-table__q">
                                                {row.question_slug?.replaceAll(
                                                    "-",
                                                    " "
                                                )}
                                            </span>
                                            <span className="adm-analytics-table__num">
                                                {fmt(row.recording_count)}
                                            </span>
                                            <span className="adm-analytics-table__num">
                                                {row.completion_rate == null
                                                    ? "—"
                                                    : fmtP(
                                                          row.completion_rate *
                                                              100
                                                      )}
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <p className="adm-analytics-empty">
                                        No recordings tracked yet.
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* ── Churn risk ── */}
                        <div className="adm-analytics-panel">
                            <SectionHead
                                title="Churn risk watchlist"
                                pill="30-day drop"
                            />
                            <div className="adm-analytics-table" role="table">
                                <div
                                    className="adm-analytics-table__head"
                                    role="row"
                                >
                                    <span role="columnheader">User</span>
                                    <span role="columnheader">
                                        Previous recordings
                                    </span>
                                    <span role="columnheader">Status</span>
                                </div>
                                {churnRisk.length > 0 ? (
                                    churnRisk.map((row, i) => (
                                        <div
                                            className="adm-analytics-table__row"
                                            key={row.id}
                                            role="row"
                                            style={{ "--i": i }}
                                        >
                                            <span className="adm-analytics-table__q">
                                                {`${row.first_name ?? ""} ${row.last_name ?? ""}`.trim() ||
                                                    row.email}
                                            </span>
                                            <span className="adm-analytics-table__num">
                                                {fmt(row.previous_count)}
                                            </span>
                                            <span>
                                                <span className="adm-analytics-badge adm-analytics-badge--warn">
                                                    At risk
                                                </span>
                                            </span>
                                        </div>
                                    ))
                                ) : (
                                    <p className="adm-analytics-empty adm-analytics-empty--good">
                                        No churn risk users detected.
                                    </p>
                                )}
                            </div>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
